import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { ProfileService } from "services/api-service"
import { ProfileType } from "services/api-service/types"
import { SentryService } from "services/SentryService"
import { RootState } from "store/store"
import styled from "styled-components"
import { Absolute, Box, Column, common } from "ui/components/atoms/_atoms"
import { IconButton } from "ui/components/atoms/button"
import { IconBox } from "ui/components/atoms/icon"
import Spinner from "ui/components/atoms/spinner"
import { P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { BaseStyles } from "ui/types/base-styles"

const LogoUploader = ({
  onSubmit,
  $background = "background",
  ...rest
}: {
  onSubmit: (e: ProfileType | null) => void
  $background?: string
} & BaseStyles) => {
  const { t } = useTranslation(["panel", "form", "common", "data"])
  const { profile } = useSelector((state: RootState) => state.profile)

  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(false)

  const handleError = () => {
    setError(true)
    setTimeout(() => {
      setError(false)
    }, 4000)
  }

  const handleDeleteFile = async () => {
    try {
      setDeleting(true)
      const profile = await ProfileService.deleteLogo()
      onSubmit(profile)
    } catch (e) {
      SentryService.error("[ERROR ProfileService.deleteLogo]", e)
    } finally {
      setDeleting(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    if (
      files &&
      ["image/png", "image/jpeg"].includes(files[0].type) &&
      files[0].size / 1024 / 1024 <= 1
    ) {
      try {
        setLoading(true)
        const profile = await ProfileService.updateLogo(files[0])
        onSubmit(profile)
      } catch (e) {
        handleError()
        SentryService.error("[ERROR ProfileService.updateLogo]", e)
      } finally {
        setLoading(false)
      }
    } else {
      handleError()
    }
  }

  return (
    <>
      <Content $background={$background} {...rest}>
        {profile?.logo ? (
          <>
            <LogoWrapper>
              <img src={profile.logo} />
            </LogoWrapper>
            <Absolute $width="max-content" $height="max-content" $top="XS" $right="XS">
              <IconButton
                icon="trash"
                $background="red"
                $color="white"
                onClick={handleDeleteFile}
                ariaLabel="Close"
                $size="45px"
                $iconSize="L"
                loading={deleting}
                $borderRadius="borderRadius"
              />
            </Absolute>
          </>
        ) : (
          <Column $justifyContent="center" $alignItems="center">
            <IconBox icon="download" $marginBottom="S" />
            <Box>
              <P $fontSize="M" $marginBottom="XXS" $textAlign="center" $fontWeight="normal">
                {t("profile.profile-logo.uploader.title")}
              </P>
              <P $fontSize="XS" $margin="0" $textAlign="center">
                {t("profile.profile-logo.uploader.text")}
              </P>
            </Box>
          </Column>
        )}
        {loading && (
          <Absolute
            $left="0"
            $top="0"
            $width="100%"
            $height="100%"
            $background={$background}
            $display="flex"
            $justifyContent="center"
            $alignItems="center"
          >
            <Spinner />
          </Absolute>
        )}
        <input
          id="file_uploader"
          type="file"
          onChange={handleFileChange}
          accept="image/png, image/jpeg"
        />
      </Content>
      {!!error && (
        <Alert $type="error" $small $marginTop="XS">
          {t("profile.profile-logo.uploader.error")}
        </Alert>
      )}
    </>
  )
}

export default LogoUploader

const LogoWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const Content = styled.label<{
  $background: string
}>`
  ${common};
  padding: ${({ theme }) => theme.space.M} ${({ theme }) => theme.space.S};
  background: ${({ theme, $background }) => theme.color[$background]};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  height: 200px;

  input[type="file"] {
    display: none;
  }
`
